import PlusOutlined from "@ant-design/icons/lib/icons/PlusOutlined";
import { FormInstance, Table, message } from "antd";
import Button from "antd/lib/button";
import Spin from "antd/lib/spin";
import React, { RefObject } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { APIError, Department, FormFieldConfig, Team, User } from "../helpers/shared-interfaces";
import { generateDisplayErrorMessage, handleValidationErrors, mapAPIErrorToFormError, request, responseHandler, showTotalPagination } from "../helpers/utilities";
import { ColumnType } from "antd/lib/table/interface";
import { CREATE_TEAM, GET_ALL_TEAMS } from "../gql/teams";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import { ALL_USERS_QUERY } from "../gql/users";
import { GET_DEPARTMENTS_WITH_USERS } from "../gql/departments";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import { PageToolbar } from "../components/PageToolbar";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface State {
  isLoading: boolean;
  teams: Team[];
  showNewTeamModal: boolean
  isCreatingTeam: boolean
  name: string;
  users: User[];
  departments: Department[];
  userSearchTerm: string;
}

export function getColumns<T extends Team>(teams: Team[]): ColumnType<T>[] {
  return [
    {
      title: "Team",
      dataIndex: "team",
      key: "team",
      render(text: string, record: Team): JSX.Element {
        return (
          <NavLink to={{ pathname: `/teams/${record.id}`, state: { teams } }}>
            <span>{record.name}</span>
          </NavLink>
        );
      },
    },
    {
      title: "Department Name",
      dataIndex: "name",
      key: "Department Name",
      width: "65%",
      render(text: string, record: Team): JSX.Element {
        return (
          <NavLink
            to={`/departments/${record?.department.id}`}
            data-cy="test-department-name-link"
          >
            {record?.department?.name}
          </NavLink>
        );
      },
    },
  ];
}

export function TeamTableFactory(data: Team[], isLoading: boolean): JSX.Element {
  const tempData: Team[] = data.map((team: Team): Team => {
    return { ...team, key: team.id };
  });
  return (
    <Table
      loading={isLoading}
      columns={getColumns(tempData)}
      dataSource={tempData}
      pagination={{
        pageSize: 10,
        showTotal: showTotalPagination,
      }}
    />
  );
}

export const NewTeamUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  isCreatingTeam: boolean
  departments: Department[]
  users: User[]
  handleUserSearchInput: (userSearchTerm: string) => Promise<void>
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      name: "name",
      rule: "string",
      inputType: "string",
      required: true,
      label: "Name",
      placeholder: "Name",
      colConfig: {
        span: 24,
      },
    },
    {
      name: "departmentId",
      rule: "string",
      inputType: "select",
      required: false,
      label: "Department",
      placeholder: "Department",
      selectOptions: props.departments,
      colConfig: {
        span: 24,
      },
    },
    {
      name: "userIds",
      inputType: "multipleSelectSearch",
      rule: "string",
      required: true,
      label: "Users",
      placeholder: "Users",
      selectOptions: props.users,
      searchHandler: props.handleUserSearchInput,
      isArrayField: true,
      colConfig: {
        span: 24,
      },
    },
  ];

  return (
    <Spin spinning={props.isCreatingTeam}>
        <BaseForm
          layout="vertical"
          reference={props.reference}
          fieldsToHide={[]}
          fields={fields}
        />
    </Spin>
  );
};


export default class AllTeams extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      isLoading: true,
      teams: [],
      showNewTeamModal: false,
      isCreatingTeam: false,
      users: [],
      departments: [],
      userSearchTerm: "",
      name: ""
    };
    this.reference = React.createRef<FormInstance>();
    this.toggleCreateTeamModal =
      this.toggleCreateTeamModal.bind(this);
  }

  getAllTeams = (): void => {
    this.setState({ isLoading: true });
    request({
      query: GET_ALL_TEAMS,
    })
      .then(({ data: { data } }: any): void => {
        this.setState({
          teams: data.teams,
        });
      })
      .catch((error) => {
        message.error("Failed to load teams");
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  componentDidMount(): void {
    this.setState({ isLoading: true }, (): void => {
      this.getAllTeams();
      this.getAllDepartments();
      this.getAllUsers();
    });
  }

  getAllDepartments = (): void => {
    request({
      query: GET_DEPARTMENTS_WITH_USERS,
    }).then(({ data: { data } }: any): void => {
      this.setState({
        departments: data.departments,
        isLoading: false,
      });
    });
  };

  getAllUsers(): void {
    request({
      query: ALL_USERS_QUERY,
      variables: {
        page: 1,
        q: this.state.userSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.users.results,
        isLoading: false,
      });
    });
  }

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);

  createTeam = async (): Promise<void> => {
    interface Response {
      data?: {
        createTeam: {
          team: Team;
          errors: APIError;
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields([
        "name",
        "departmentId",
        "userIds",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_TEAM,
        variables: data,
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "createTeam",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An error occurred. The team could not be created");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Team created sucessfully");
        this.getAllTeams()
      }
    } catch (error) {
      message.error(`Failed to create team - ${generateDisplayErrorMessage(error)}`);
    } finally {
      this.setState({ isLoading: false });
      this.toggleCreateTeamModal()
    }
  };

  toggleCreateTeamModal(): void {
    this.setState((prevState) => ({
      showNewTeamModal: !prevState.showNewTeamModal
    }))
  }

  render(): JSX.Element | null {
    const { teams, isLoading, showNewTeamModal, isCreatingTeam, departments, users } = this.state;
    return (
        <Card>
          <div className="departments-table-container">
              <PageToolbar modelName="teams"
                primaryAction={
                  <EntitledSection entitlement={entitlements.Companies.teams.create}>
                    <Button
                      type="primary"
                      onClick={this.toggleCreateTeamModal}
                      data-cy="test-new-user-button"
                    >
                      <PlusOutlined /> Create Team
                    </Button>
                  </EntitledSection>
                }
                totalCount={teams.length}
                currentDataCount={teams.length}
                page={1}
                pageSize={teams.length}
              />
            {TeamTableFactory(teams, isLoading)}
            {showNewTeamModal &&
              ModalCreator({
                title: "Create Team",
                okType: "primary",
                okText: "Create",
                showModal: showNewTeamModal,
                onOkay: this.createTeam,
                toggleModal: this.toggleCreateTeamModal,
                inscription: NewTeamUI({
                  reference: this.reference,
                  isCreatingTeam,
                  departments,
                  users,
                  handleUserSearchInput: this.handleUserSearchInput
                }),
              })}
          </div>
        </Card>
    );
  }
}
